import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState, ThunkApi } from 'state/store';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import { FormSummary } from 'types/store/normalized';

/* ============================== STATE SETUP =============================== */
interface FormSummariesState {
  status: Status;
  links?: { next: string };
  cursor?: string;
}

const initialState: FormSummariesState = {
  status: SLICE_STATUS.IDLE,
};

const adapter = createEntityAdapter<FormSummary>();

/* ============================== REDUX THUNK =============================== */
function concurrencyCondition(
  thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean {
  const { status } = getState().normalized.formSummaries;
  return status !== SLICE_STATUS.LOADING;
}

interface GetListParams {
  formIds?: string[];
  search?: string;
  pageSize?: number;
  fetchNext?: boolean;
}

interface GetFormSummariesResponse {
  data: FormSummary[];
  totalCount: number;
  links: { next: string };
  cursor: string;
}

const NO_OP_RESPONSE: GetFormSummariesResponse = {
  data: [],
  totalCount: 0,
  cursor: '',
  links: { next: '' },
};

export const getFormSummaries = createAsyncThunk<
  GetFormSummariesResponse,
  GetListParams,
  ThunkApi
>(
  'normalized/formSummary/getFormSummaries',
  async (
    { formIds, search, pageSize, fetchNext = false },
    { getState, extra: { getFormSummariesApi } }
  ) => {
    const state = getState();

    let notCached: string[] | undefined = undefined;

    /**
     * INVARIANT: /v2/form-summaries validates every element of `formIds` as a
     * GUID and rejects the WHOLE request (`"formIds[n]" must be a valid GUID`)
     * if one is null/empty — and request rows can carry a null `formId`, so
     * never send the raw list.
     *
     * Omitting `formIds` means "fetch the unfiltered list" (the filter
     * dropdowns); an explicit list that cleans down to nothing must NOT fall
     * through to that, or one null formId would trigger a full fetch.
     */
    if (formIds !== undefined) {
      const cleanedIds = formIds.filter(
        (id): id is string => typeof id === 'string' && id.trim() !== ''
      );

      notCached = Array.from(new Set(cleanedIds)).filter(
        (id) => !state.normalized.formSummaries.entities[id]
      );

      if (notCached.length === 0) {
        return NO_OP_RESPONSE;
      }
    }

    const { data, meta, links } = await getFormSummariesApi().getList({
      params: {
        formIds: notCached,
        search,
        pageSize,
        after: fetchNext ? state.normalized.formSummaries.cursor : undefined,
      },
    });
    return { data, totalCount: meta.totalCount, cursor: meta.cursor, links };
  },
  { condition: concurrencyCondition }
);

/* ================================= REDUCER ================================ */
const formSummariesSlice = createSlice({
  name: 'normalized/formSummary',
  initialState: adapter.getInitialState(initialState),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getFormSummaries.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(getFormSummaries.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(getFormSummaries.fulfilled, (state, action) => {
        adapter.upsertMany(state, action.payload.data);
        state.status = SLICE_STATUS.SUCCESS;
        state.links = action.payload.links;
        state.cursor = action.payload.cursor;
      });
  },
});

/* =============================== SELECTORS ================================ */
const defaultSelectors = adapter.getSelectors<RootState>(
  (state) => state.normalized.formSummaries
);

const selectStatus = (state: RootState) =>
  state.normalized.formSummaries.status;

const selectCanFetchMore = (state: RootState) =>
  !!state.normalized.formSummaries.links?.next;

export const formSummarySelectors = {
  ...defaultSelectors,
  selectStatus,
  selectCanFetchMore,
};

export default formSummariesSlice.reducer;
